import { computed, makeObservable } from 'mobx';
import { Geometry } from '../types/Geometry';
import FieldState from '../../common/store/FieldState';

type Geolocation = Pick<Geometry, 'clat' | 'clon'>;

type GeolocationParams = {
  clat: string;
  clon: string;
};

/**
  * Rounds the precision to 5 decimals and truncates trailing zeroes.
  */
const normalizeCoordinate = (value: number): string => value.toFixed(5).replace(/\.?0+$/, '');

const GEOLOCATION_KEYS: (keyof Geolocation)[] = ['clat', 'clon'];

/**
  * Converts all value properties on the geolocation object to strings.
  */
export const toString = (geolocation: Partial<Geolocation>): Partial<GeolocationParams> => (
  geolocation ? Object.keys(geolocation).reduce(
    (newGeolocation: GeolocationParams, key: keyof Geolocation) => {
      if (GEOLOCATION_KEYS.includes(key)) {
        // eslint-disable-next-line no-param-reassign
        newGeolocation[key] = (geolocation[key] != null
          ? normalizeCoordinate(geolocation[key]) : null);
      }
      return newGeolocation;
    },
    {} as GeolocationParams,
  ) : {});

/**
  * Converts all value properties on the geolocation object to numbers.
  */
export const toNumber = (geolocation: GeolocationParams): Geolocation | null => (
  geolocation ? Object.keys(geolocation).reduce(
    (newGeolocation: Geolocation, key: keyof Geolocation) => {
      if (GEOLOCATION_KEYS.includes(key)) {
        // eslint-disable-next-line no-param-reassign
        newGeolocation[key] = (geolocation[key] != null ? Number(geolocation[key]) : null);
      }
      return newGeolocation;
    },
    {} as Geolocation,
  ) : null);

const isGeolocationParams = (geolocation: any): geolocation is GeolocationParams => (
  geolocation
  && GEOLOCATION_KEYS.every(
    (key) => typeof geolocation[key] === 'string',
  )
);

const isGeolocation = (geolocation: any): geolocation is Geolocation => (
  geolocation
  && GEOLOCATION_KEYS.every(
    (key) => typeof geolocation[key] === 'number',
  )
);

/**
  * Ensures all the required values are available before parsing them to geolocation.
  * If any of the values are missing, the geolocation is set to `null`.
  */
export const validateGeolocation = (
  geolocation?: Geolocation | GeolocationParams | null,
): Geolocation | null => {
  if (isGeolocationParams(geolocation)) {
    return toNumber(geolocation);
  }
  if (isGeolocation(geolocation)) {
    return geolocation;
  }
  return null;
};

/**
  * Stores the user geolocation data.
  */
class GeolocationFieldState extends FieldState<Geolocation | null> {
  constructor(geolocation: GeolocationParams | null = null) {
    super(validateGeolocation(geolocation));
    makeObservable(this, {
      query: computed,
    });
  }

  /**
    * Converts all the numeric values to strings to be used in creating the
    * query string params.
    */
  public get query(): Partial<GeolocationParams> {
    return toString(this.value);
  }
}

export default GeolocationFieldState;
